import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Users } from 'lucide-react';
import { teamService } from '../services/teamService';
import { assessmentService } from '../services/assessmentService';
import logoImage from '../assets/logo.svg';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface OnboardingState {
  teamId: string;
  teamName: string;
  players: { id: string; firstName: string }[];
}

export const OnboardingCompletePage: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [unassessed, setUnassessed] = useState<{ id: string; firstName: string; lastName: string; position: string }[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [assessedPlayer, setAssessedPlayer] = useState('');
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<'select' | 'complete'>('select');

  useEffect(() => {
    const raw = localStorage.getItem('gamepath_onboarding');
    if (!raw) { navigate('/dashboard'); return; }
    const data: OnboardingState = JSON.parse(raw);
    setState(data);

    // Check which players still need assessments
    const checkPlayers = async () => {
      try {
        const players = await teamService.getTeamPlayers(data.teamId);
        const needAssessment: typeof unassessed = [];
        for (const p of players) {
          try {
            const assessments = await assessmentService.getPlayerAssessments(p.id);
            if (!assessments.some(a => a.isComplete)) {
              needAssessment.push({ id: p.id, firstName: p.firstName, lastName: p.lastName, position: p.position });
            } else if (data.players.some(dp => dp.id === p.id)) {
              setAssessedPlayer(`${p.firstName} ${p.lastName}`);
            }
          } catch { needAssessment.push({ id: p.id, firstName: p.firstName, lastName: p.lastName, position: p.position }); }
        }
        setUnassessed(needAssessment);
        if (needAssessment.length === 0) setPhase('complete');
        else setPhase('select');
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    checkPlayers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssessAnother = () => {
    if (selectedPlayer) navigate(`/onboarding/assess/${selectedPlayer}`);
  };

  const handleSkip = () => {
    localStorage.setItem('gamepath_show_assess_hint', 'true');
    localStorage.removeItem('gamepath_onboarding');
    navigate('/dashboard');
  };

  const handleFinish = () => {
    localStorage.removeItem('gamepath_onboarding');
    navigate('/dashboard');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center pt-8 px-4">
      <img src={logoImage} alt="GamePath" className="h-8 mb-2" />
      {phase === 'complete' || unassessed.length === 0 ? (
        <>
          <p className="text-sm text-gray-500 mb-6">Onboarding complete</p>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 lg:p-8 max-w-lg w-full">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Excellent Work!</h2>
              <p className="text-gray-500 text-sm">You've successfully completed the onboarding process:</p>
            </div>
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 p-3 border-b border-gray-100">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Team Created</p>
                  <p className="text-sm text-gray-500">{state?.teamName} set up</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border-b border-gray-100">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Players Added</p>
                  <p className="text-sm text-gray-500">{state?.players.length} player{(state?.players.length || 0) !== 1 ? 's' : ''} in your squad</p>
                </div>
              </div>
              {assessedPlayer && (
                <div className="flex items-center gap-3 p-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">First Assessment Complete</p>
                    <p className="text-sm text-gray-500">{assessedPlayer} baseline established</p>
                  </div>
                </div>
              )}
            </div>
            <button onClick={handleFinish}
              className="w-full px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]">
              Go to Dashboard
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-6">Select a player to assess</p>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 lg:p-8 max-w-lg w-full">
            {assessedPlayer && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 text-center">
                <p className="text-sm text-green-800">
                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                  {assessedPlayer}'s assessment complete!
                </p>
              </div>
            )}
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Select Player for First Assessment</h2>
              <p className="text-gray-500 text-sm">Choose which player you'd like to assess first</p>
            </div>
            <div className="space-y-2 mb-6">
              {unassessed.map(p => (
                <button key={p.id} onClick={() => setSelectedPlayer(p.id)}
                  className={`w-full text-left p-4 rounded-lg border transition-colors min-h-[44px] ${
                    selectedPlayer === p.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <p className="font-medium text-gray-900">{p.firstName} {p.lastName}</p>
                  <p className="text-sm text-gray-500">{p.position}</p>
                </button>
              ))}
            </div>
            <button onClick={handleAssessAnother} disabled={!selectedPlayer}
              className="w-full px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed mb-3">
              Start Assessment
            </button>
            <button onClick={handleSkip}
              className="w-full px-6 py-3 text-gray-600 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors min-h-[44px] mb-2">
              Skip for Now
            </button>
            <p className="text-xs text-gray-400 text-center">You can always assess players from your dashboard.</p>
          </div>
        </>
      )}
    </div>
  );
};
